import React, { Component, Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import i18n from 'cityads-v3/i18n';

import Collapse from '@mui/material/Collapse'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography';

import CloseIcon from '@mui/icons-material/Close';

import MaskedTextField from 'cityads-v3/components/MaskedTextField';
import WaitingButton from 'cityads-v3/components/Buttons/WaitingButton';
import withHooks from 'cityads-v3/decorators/withHooks';

import { amplitudeSendEvent } from 'cityads-v3/utils/counterUtils';
import {
  SMS_RESULT_OK,

  SMS_STATE_IDLE,
  SMS_STATE_WAITING,
  SMS_STATE_AWAITING_FIRST_CODE_REQUEST,
  SMS_STATE_AWAITING_REPEATED_CODE_REQUEST,
  SMS_STATE_ENTERING_CODE,
  SMS_STATE_AWAITING_CODE_SEND,
} from 'cityads-v3/utils/smsUtils';

import classes from './SmsDialog.scss';

import {
  setPhone,
  setCode,
  requestConfirmationCode,
  cleanErrors,
  cleanLastRequestTime,
} from 'cityads-v3/actions/sms';

const PHONE_CONFIRMATION_SMS_LENGTH  = 4;
const ACTION_CONFIRMATION_SMS_LENGTH = 4;

const SMS_STATES_FOR_PHONE_ENTER = new Set([
  SMS_STATE_WAITING,
]);

const SMS_STATES_FOR_CODE_DISPLAY = new Set([
  SMS_STATE_AWAITING_REPEATED_CODE_REQUEST,
  SMS_STATE_ENTERING_CODE,
  SMS_STATE_AWAITING_CODE_SEND,
]);

const SMS_STATES_FOR_CODE_ENTER = new Set([
  SMS_STATE_AWAITING_REPEATED_CODE_REQUEST,
  SMS_STATE_ENTERING_CODE,
]);

const SMS_STATES_AWAITING_CODE_REQUEST = new Set([
  SMS_STATE_AWAITING_FIRST_CODE_REQUEST,
  SMS_STATE_AWAITING_REPEATED_CODE_REQUEST,
]);

const SMS_REQUEST_INTERVAL = 60000;

export const SMS_DIALOG_VARIANT_CREATE_ACCOUNT = 'create_account';
export const SMS_DIALOG_VARIANT_CREATE_PAYMENT = 'create_payment';

export class SmsDialog extends Component {
  static propTypes = {
    open             : PropTypes.bool.isRequired,
    onClose          : PropTypes.func.isRequired,
    onConfirmAction  : PropTypes.func.isRequired,
    onActionConfirmed: PropTypes.func.isRequired,

    variant: PropTypes.oneOf([
      SMS_DIALOG_VARIANT_CREATE_ACCOUNT,
      SMS_DIALOG_VARIANT_CREATE_PAYMENT,
    ]).isRequired,

    isCodeWrong        : PropTypes.bool.isRequired,
    isPhoneConfirmed   : PropTypes.bool.isRequired,
    smsState           : PropTypes.oneOf([
      SMS_STATE_IDLE,
      SMS_STATE_WAITING,
      SMS_STATE_AWAITING_FIRST_CODE_REQUEST,
      SMS_STATE_AWAITING_REPEATED_CODE_REQUEST,
      SMS_STATE_ENTERING_CODE,
      SMS_STATE_AWAITING_CODE_SEND,
    ]).isRequired,
    lastCodeRequestTime: PropTypes.number,
    smsErrors          : PropTypes.array.isRequired,
    smsErrorsTranslated: PropTypes.bool,

    requestConfirmationCode: PropTypes.func.isRequired,

    t: PropTypes.func.isRequired,
  };

  state = {
    currentTime    : Number(new Date()),
    isPhoneComplete: false,
    isCodeComplete : false,
  };

  componentDidMount() {
    this.timerId = setInterval(
      () => this.setState({
        currentTime: Number(new Date()),
      }, () => {
        this.resetIfTimeIsOver();
      }),
      1000
    );
  }

  componentWillUnmount() {
    clearInterval(this.timerId);
  }

  onClose = (evt) => {
    const {
      setCode,
      onClose,
      cleanErrors,
      cleanLastRequestTime,
    } = this.props;

    setCode('');
    cleanErrors();
    cleanLastRequestTime();

    onClose(evt);
  }

  onPhoneAccept = (value, mask, evt) => {
    this.props.setPhone(value);

    this.setState({
      isPhoneComplete: mask.masked.isComplete,
    });
  };

  onPhoneKeyDown = (evt) => {
    const { smsState } = this.props;

    if (evt.key === 'Enter' && smsState === SMS_STATE_WAITING) {
      this.onRequestConfirmationCode(evt);
    }
  };

  onCodeAccept = (value, mask, evt) => {
    this.props.setCode(value);

    this.setState({
      isCodeComplete: mask.masked.isComplete,
    });
  };

  onCodeKeyDown = (evt) => {
    const { smsState } = this.props;

    if (evt.key === 'Enter' && smsState !== SMS_STATE_AWAITING_CODE_SEND) {
      this.onSendConfirmationCode(evt);
    }
  };

  resetIfTimeIsOver() {
    const { lastCodeRequestTime } = this.props;

    if (lastCodeRequestTime === null || this.getIsCodeRequestTooSoon()) {
      this.wasResetPerformed = false;
      return;
    }

    if (this.wasResetPerformed) {
      return;
    }

    this.wasResetPerformed = true;
  }

  getIsCodeRequestTooSoon() {
    const { lastCodeRequestTime } = this.props;
    const { currentTime } = this.state;

    return lastCodeRequestTime !== null && currentTime - lastCodeRequestTime < SMS_REQUEST_INTERVAL;
  }

  getSubtitle() {
    const { variant, t } = this.props;

    switch (variant) {
      case SMS_DIALOG_VARIANT_CREATE_ACCOUNT:
      case SMS_DIALOG_VARIANT_CREATE_PAYMENT:
        return t(`sms.subtitle.${variant}`);

      default:
        return null;
    }

  }

  formatRemainingTime() {
    const { lastCodeRequestTime } = this.props;
    const { currentTime } = this.state;

    const MINUTE_LENGTH = 60;

    const remainingTime = Math.round((SMS_REQUEST_INTERVAL - (currentTime - lastCodeRequestTime)) / 1000);
    const remainingMinutes = Math.floor(remainingTime / MINUTE_LENGTH);
    const remainingSeconds = remainingTime % MINUTE_LENGTH;

    return `${remainingMinutes}:${remainingSeconds >= 10 ? remainingSeconds : `0${remainingSeconds}`}`;
  }

  onRequestConfirmationCode = (evt) => {
    const {
      smsState,
      requestConfirmationCode,
      onConfirmAction,
    } = this.props;

    if (smsState === SMS_STATE_ENTERING_CODE) {
      requestConfirmationCode();
    } else {
      onConfirmAction();
    }
  }

  onSendConfirmationCode = async (evt) => {
    const {
      isPhoneConfirmed,
      onConfirmAction,
      onActionConfirmed,
    } = this.props;

    const { sms: smsResult } = await onConfirmAction();

    if (smsResult?.result === SMS_RESULT_OK) {
      amplitudeSendEvent('makeConversions', 'd-v1-e44', 'verification', 'sms_success', {
        form_type: isPhoneConfirmed ? 'sms verification' : 'account verification'
      });

      await onActionConfirmed();
    }
  }

  getCodePlaceholder(maxLength) {
    let placeholder = '';

    for (let i = 1; i <= maxLength; i++) {
      placeholder += String(i);
    }

    return placeholder;
  }

  renderErrors(errors, isTranslationRequired) {
    if (!errors || !errors.length) {
      return null;
    }

    const { t } = this.props;

    return (
      <>
        {errors.map((err, index) => (
          <Fragment key={index}>
            {isTranslationRequired ? t(err) : err}<br />
          </Fragment>
        ))}
      </>
    )
  }

  render() {
    const {
      t,

      open,

      phone,
      code,
      isCodeWrong,
      isPhoneConfirmed,
      smsState,
      smsErrors,
      smsErrorsTranslated,
    } = this.props;

    const phoneErrors = (!isPhoneConfirmed && smsState === SMS_STATE_WAITING) ? (smsErrors ?? []) : [];
    const codeErrors = (!isPhoneConfirmed && smsState === SMS_STATE_WAITING) ? [] : (smsErrors ?? []);
    const maxCodeLength = isPhoneConfirmed ? ACTION_CONFIRMATION_SMS_LENGTH : PHONE_CONFIRMATION_SMS_LENGTH;

    const isCodeRequestTooSoon = this.getIsCodeRequestTooSoon();
    const subtitle = this.getSubtitle();

    return (
      <Dialog
        open    = {open && smsState !== SMS_STATE_IDLE}
        onClose = {this.onClose}
        classes = {{ paper: classes.dialogPaper }}
        aria-labelledby = "sms-dialog-title"
      >
        <IconButton onClick={this.onClose} className={classes.close} size="large">
          <CloseIcon />
        </IconButton>
        <DialogTitle id="payment-dialog-title" classes={{ root: classes.title }}>
          <Typography variant="h3" className={classes.mainTitle}>
            {t('sms.title')}
          </Typography>
          {subtitle ?
            (
              <Typography variant="subtitle1" color="textSecondary" className={classes.subtitle}>
                {subtitle}
              </Typography>
            ) :
            null
          }
        </DialogTitle>
        <DialogContent classes={{ root: classes.content }}>
          <Collapse in={!isPhoneConfirmed} mountOnEnter unmountOnExit>
            <MaskedTextField
              value      = {phone}
              onKeyDown  = {this.onPhoneKeyDown}
              disabled   = {!SMS_STATES_FOR_PHONE_ENTER.has(smsState)}
              label      = {t('sms.phoneNumber')}
              helperText = {this.renderErrors(phoneErrors, !smsErrorsTranslated)}
              error      = {!!phoneErrors.length}
              fullWidth
              classes = {{
                root: classes.textFieldRoot
              }}
              InputProps = {{
                autoFocus: true,
                inputProps: {
                  onAccept: this.onPhoneAccept,
                  mask: '{+}00000000000[0][0][0]',
                  lazy: false,
                  unmask: true,
                },
                classes: {
                  root: classes.inputRoot,
                  input: classes.input,
                }
              }}
              InputLabelProps = {{
                shrink: true,
                classes: {
                  root: classes.inputLabel,
                }
              }}
              FormHelperTextProps = {{
                classes: {
                  root: classes.inputHelper
                }
              }}
            />
          </Collapse>
          <Collapse in={isPhoneConfirmed || SMS_STATES_FOR_CODE_DISPLAY.has(smsState)} mountOnEnter unmountOnExit>
            <MaskedTextField
              value       = {code}
              onKeyDown   = {this.onCodeKeyDown}
              disabled    = {!SMS_STATES_FOR_CODE_ENTER.has(smsState)}
              label       = {t('sms.smsCode')}
              placeholder = {this.getCodePlaceholder(maxCodeLength)}
              helperText  = {this.renderErrors(codeErrors, !smsErrorsTranslated)}
              error       = {!!codeErrors.length}
              fullWidth
              classes = {{
                root: classes.textFieldRoot
              }}
              InputProps = {{
                autoFocus: true,
                inputProps: {
                  onAccept: this.onCodeAccept,
                  mask: '0'.repeat(maxCodeLength),
                  lazy: false,
                  unmask: true,
                },
                classes: {
                  root: classes.inputRoot,
                  focused: classes.inputRootFocused,
                  disabled: classes.inputRootDisabled,
                  error: classes.inputRootError,
                  input: classes.input,
                }
              }}
              InputLabelProps = {{
                shrink: true,
                classes: {
                  root: classes.inputLabel,
                }
              }}
              FormHelperTextProps = {{
                classes: {
                  root: classes.inputHelper
                }
              }}
            />
          </Collapse>
          <div className={classes.buttons}>
            {(SMS_STATES_FOR_CODE_DISPLAY.has(smsState)) ?
              (
                <WaitingButton
                  key       = "confirm"
                  className = {classes.button}
                  variant   = "contained"
                  color     = "primary"
                  onClick   = {this.onSendConfirmationCode}
                  disabled  = {smsState === SMS_STATE_AWAITING_CODE_SEND || isCodeWrong}
                  waiting   = {smsState === SMS_STATE_AWAITING_CODE_SEND}
                >
                  {t('sms.confirm')}
                </WaitingButton>
              ) :
              null
            }
            {(SMS_STATES_FOR_CODE_DISPLAY.has(smsState)) ?
              (
                <WaitingButton
                  key       = "sendCodeAgain"
                  className = {classes.button}
                  onClick   = {this.onRequestConfirmationCode}
                  disabled  = {smsState !== SMS_STATE_ENTERING_CODE || isCodeRequestTooSoon}
                  waiting   = {SMS_STATES_AWAITING_CODE_REQUEST.has(smsState)}
                >
                  {isCodeRequestTooSoon ? t('sms.sendCodeAgainIn', { time: this.formatRemainingTime() }) : t('sms.sendCodeAgain')}
                </WaitingButton>
              ) :
              (
                <WaitingButton
                  key       = "sendCode"
                  className = {classes.button}
                  variant   = "contained"
                  color     = "primary"
                  onClick   = {this.onRequestConfirmationCode}
                  disabled  = {smsState !== SMS_STATE_WAITING || isCodeRequestTooSoon}
                  waiting   = {SMS_STATES_AWAITING_CODE_REQUEST.has(smsState)}
                >
                  {isCodeRequestTooSoon ? t('sms.sendCodeIn', { time: this.formatRemainingTime() }) : t('sms.sendCode')}
                </WaitingButton>
              )
            }
          </div>
        </DialogContent>
      </Dialog>
    )
  }
}

export default compose(
  connect(
    (state, props) => ({
      phone              : state.sms.phone,
      code               : state.sms.code,
      isCodeWrong        : state.sms.isCodeWrong,
      isPhoneConfirmed   : state.sms.isPhoneConfirmed,
      smsState           : state.sms.smsState,
      lastCodeRequestTime: state.sms.lastCodeRequestTime,
      smsErrors          : state.sms.errors,
    }),
    dispatch => bindActionCreators({
      setPhone,
      setCode,
      requestConfirmationCode,
      cleanErrors,
      cleanLastRequestTime,
    }, dispatch)
  ),
  withHooks(props => {
    const { open, isPhoneConfirmed } = props;
    const isPhoneConfirmedRef = useRef(null);

    isPhoneConfirmedRef.current = isPhoneConfirmed;

    useEffect(() => {
      if (open) {
        amplitudeSendEvent('makeConversions', 'd-v1-e6', 'verification', 'sms_form_view', {
          form_type: isPhoneConfirmedRef.current ? 'sms verification' : 'account verification'
        });
      }
    }, [open]);

    return props;
  }),
  withTranslation(['main'], { i18n, wait: !!window }),
)(SmsDialog);
